import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { map, take } from "rxjs/operators";
import { AppService } from "../apps/app.service";
import { LoggedInGuard } from "../auth/auth.guards";
import { AccountPrerequisitesGuard } from "../prerequisites/account-prerequisites.guard";
import { SessionService } from "../session.service";
import { SignalsService } from "../signals/signals.service";
import { UtilService } from "../util/util.service";
import { AudienceListComponent } from "./audiences/audience-list/audience-list.component";
import { AudienceComponent } from "./audiences/audience/audience.component";
import { BroadcastPerformanceComponent } from "./broadcast-performance/broadcast-performance.component";
import { BroadcastComponent } from "./broadcast/broadcast.component";
import { BroadcastService } from "./broadcast.service";
import { GeofenceService } from "../geofences/geofence.service";
import { BeaconRegionsService } from "../beacon-regions/beacon-regions.service";
import { BroadcastListComponent } from "./broadcast-list/broadcast-list.component";
import { PermissionService } from "../permission.service";
import { TemplateListGuard } from "../content/content.routes";
import { AccountSignalCategoryListGuard } from "../shared/guards/account-signal-category.guards";
import {
  BroadcastDetailGuard,
  BroadcastListGuard,
  BroadcastWriteGuard,
} from "./broadcasts.guards";
import { ViewAudienceComponent } from "./audiences/view-audience/view-audience.component";
import { IntegrationsService } from "../integrations/integrations.service";

@Injectable({ providedIn: "root" })
export class SiganlTemplatesResolve implements Resolve<any> {
  constructor(
    private SignalsService: SignalsService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.SignalsService.getSignalTemplates(
      this.SessionService.currentAccountValue!.accountID,
      { page: 0, perPage: 999999999 },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class InitialAudienceResolve implements Resolve<any> {
  constructor(private router: Router) {}

  resolve() {
    return this.router.getCurrentNavigation()?.extras.state?.audience;
  }
}

@Injectable({ providedIn: "root" })
export class SignalCategoriesResolve implements Resolve<any> {
  constructor(
    private SignalsService: SignalsService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.SignalsService.getSignalCategories(
      this.SessionService.currentAccountValue!.accountID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AccountAppResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.AppService.getApp(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class TagsResolve implements Resolve<any> {
  constructor(
    private UtilService: UtilService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.UtilService.getTags(
      this.SessionService.currentAccountValue!.accountID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BroadcastsResolve implements Resolve<any> {
  constructor(
    private BroadcastService: BroadcastService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    const facilityIDs = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];

    const startDate = new Date();
    startDate.setMonth(new Date().getMonth() - 1);

    const endDate = new Date();

    return this.BroadcastService.getBroadcasts(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        facility_id: facilityIDs,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BroadcastsListResolve implements Resolve<any> {
  constructor(
    private BroadcastService: BroadcastService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    const facilityIDs = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];

    return this.BroadcastService.getBroadcasts(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        startDate: new Date("January 1 1950").toISOString(),
        endDate: new Date("December 31 3000").toISOString(),
        facility_id: facilityIDs,
        listView: true,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BroadcastResolve implements Resolve<any> {
  constructor(
    private BroadcastService: BroadcastService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.BroadcastService.getBroadcast(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      route.params.broadcastID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class GeofencesResolve implements Resolve<any> {
  constructor(
    private GeofenceService: GeofenceService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    const facilityIDs = this.SessionService.selectedFacilities?.length
      ? this.SessionService.selectedFacilities!.map((facility) => {
          return facility.id;
        })
      : [];

    return this.GeofenceService.getGeofences(
      this.SessionService.currentAccountValue!.accountID,
      {
        facility_id: facilityIDs,
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class BeaconRegionsResolve implements Resolve<any> {
  constructor(
    private BeaconRegionsService: BeaconRegionsService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    const facilityIDs = this.SessionService.selectedFacilities?.length
      ? this.SessionService.selectedFacilities!.map((facility) => {
          return facility.id;
        })
      : [];

    return this.BeaconRegionsService.getBeaconRegions(
      this.SessionService.currentAccountValue!.accountID,
      {
        facility_id: facilityIDs,
        page: 0,
        perPage: 9999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AudiencesResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.AppService.getAudiences(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AudienceResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AppService.getAudience(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      route.params.audienceID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
class PermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.beaconRegions.hasList(),
      this.PermissionService.categories.geofences.hasList(),
      this.PermissionService.categories.broadcasts.hasWrite(),
    ]).then(([hasBrList, hasGfList, hasQsWrite]) => {
      return {
        hasBrList,
        hasGfList,
        hasQsWrite,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
class AudienceListPermissions implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.broadcasts.hasWrite(),
      this.PermissionService.categories.broadcasts.hasDelete(),
      this.PermissionService.categories.broadcasts.hasList(),
      this.PermissionService.categories.accountSignalCategories.hasList(),
      this.PermissionService.categories.templates.hasList(),
    ]).then(
      ([
        hasBroadcastWrite,
        hasBroadcastDelete,
        hasBroadcastList,
        hasCategoriesList,
        hasTemplatesList,
      ]) => {
        return {
          hasBroadcastWrite,
          hasBroadcastDelete,
          hasBroadcastList,
          hasCategoriesList,
          hasTemplatesList,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
class BroadcastPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.templates.hasWrite(),
      this.PermissionService.categories.geofences.hasList(),
      this.PermissionService.categories.beaconRegions.hasList(),
    ]).then(([hasTemplatesWrite, hasGeofencesList, hasBeaconRegionsList]) => {
      return {
        hasTemplatesWrite,
        hasGeofencesList,
        hasBeaconRegionsList,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
export class BroadcastsIntegrationsResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.IntegrationsService.getIntegrations(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      false
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard, AccountPrerequisitesGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "broadcasts" },
    children: [
      {
        path: "performance",
        component: BroadcastPerformanceComponent,
        resolve: {
          broadcasts: BroadcastsResolve,
        },
        canActivate: [BroadcastListGuard],
        data: { title: "Broadcast Performance" },
      },
      {
        path: "list",
        component: BroadcastListComponent,
        // resolve: {
        //   broadcasts: BroadcastsListResolve,
        // },
        canActivate: [BroadcastListGuard],
        data: { title: "Broadcast List" },
      },
      {
        path: "audiences",
        children: [
          {
            path: "list",
            component: AudienceListComponent,
            resolve: {
              audiences: AudiencesResolve,
              permissions: AudienceListPermissions,
            },
            canActivate: [BroadcastListGuard],
            data: { title: "Audience List" },
          },
          {
            path: "create",
            component: AudienceComponent,
            data: { title: "Create Audience" },
            canActivate: [BroadcastWriteGuard],
            resolve: {
              geofences: GeofencesResolve,
              beaconRegions: BeaconRegionsResolve,
              permissions: PermissionsResolve,
            },
          },
          {
            path: ":audienceID",
            component: AudienceComponent,
            canActivate: [BroadcastWriteGuard],
            resolve: {
              geofences: GeofencesResolve,
              beaconRegions: BeaconRegionsResolve,
              audience: AudienceResolve,
              permissions: PermissionsResolve,
            },
            data: { title: "Audience" },
          },
          {
            path: "view",
            children: [
              {
                path: ":audienceID",
                component: ViewAudienceComponent,
                data: { title: "View Audience" },
                canActivate: [BroadcastDetailGuard],
                resolve: {
                  geofences: GeofencesResolve,
                  beaconRegions: BeaconRegionsResolve,
                  permissions: PermissionsResolve,
                  audience: AudienceResolve,
                },
              },
            ]
          }
        ],
      },
      {
        path: "create",
        component: BroadcastComponent,
        resolve: {
          signalTemplates: SiganlTemplatesResolve,
          signalCategories: SignalCategoriesResolve,
          accountApp: AccountAppResolve,
          tags: TagsResolve,
          audiences: AudiencesResolve,
          geofences: GeofencesResolve,
          beaconRegions: BeaconRegionsResolve,
          initialAudience: InitialAudienceResolve,
          permissions: BroadcastPermissionsResolve,
          integrations: BroadcastsIntegrationsResolve,
        },
        canActivate: [
          BroadcastWriteGuard,
          TemplateListGuard,
          BroadcastListGuard,
          AccountSignalCategoryListGuard,
        ],
        data: { title: "Create Broadcast" },
      },
      {
        path: ":broadcastID",
        component: BroadcastComponent,
        data: { title: "Broadcast" },
        canActivate: [
          BroadcastWriteGuard,
          TemplateListGuard,
          BroadcastListGuard,
          AccountSignalCategoryListGuard,
          BroadcastDetailGuard,
        ],
        resolve: {
          signalTemplates: SiganlTemplatesResolve,
          signalCategories: SignalCategoriesResolve,
          accountApp: AccountAppResolve,
          tags: TagsResolve,
          audiences: AudiencesResolve,
          geofences: GeofencesResolve,
          beaconRegions: BeaconRegionsResolve,
          broadcast: BroadcastResolve,
          permissions: BroadcastPermissionsResolve,
          integrations: BroadcastsIntegrationsResolve,
        },
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class BroadcastsRoutingModule {}

const broadcastsModuleLazyLoadRoutes: Routes = [
  {
    path: "broadcasts",
    loadChildren: () =>
      import("./broadcasts.module").then((m) => m.BroadcastsModule),
  },
];

export { broadcastsModuleLazyLoadRoutes };
